import { getApiUrl } from '@/lib/api-config';
import { entityQueryParams, type EntityQuery } from '@/lib/entity-api';

/**
 * Budget against the backend:
 *   - GET /api/budgets/overview     one row per project: its budget with what is committed / spent (+ KPIs over every project)
 *   - GET /api/budgets/financials   committed / actual per cost code of ONE project (purchase orders and bills, never counted twice)
 * Reading and writing the budget itself (its lines) is the generic entity API ('budget'). Nothing here talks to Supabase.
 */

export interface BudgetOverviewRow {
  id: string;
  project_id: string;
  project_name: string;
  project_status: string;
  has_budget: boolean;
  budget: { id: string; status: string; line_count: number; updated_at: string } | null;
  budget_total: number;
  committed: number;
  actual: number;
  /** budget_total - actual */
  remaining: number;
  /** budget_total - committed */
  available: number;
  updated_at: string | null;
}

export interface BudgetOverviewSummary {
  total: number;
  with_budget: number;
  without_budget: number;
  budget_total: number;
  committed: number;
  actual: number;
  remaining: number;
  available: number;
}

export interface CostCodeFinancials {
  /** the TOTAL value of the purchase orders on this cost code (billing them does not reduce it) */
  committed: number;
  /** what is left of that value after the bills of those orders */
  committed_open?: number;
  /** the Budget's Committed for this cost code = open on the valid orders + everything billed (a bill an order already covers is not counted twice) */
  commitment?: number;
  /** the part of it shown on the "POs / Bills" row (open + billed by bills linked to an order) and on the "Bills (no PO)" row (billed by bills with no order) */
  commitment_orders?: number;
  commitment_other?: number;
  /** everything billed on this cost code (bills, never payments) -- shown as "Billed" */
  actual: number;
  /** of which billed by bills linked to purchase orders */
  billed_po?: number;
  /** of which billed by bills with no purchase order */
  billed_other?: number;
  /** the orders and bills behind the figures (see `documents` for their names) */
  purchase_orders?: Array<{ id: string; committed: number }>;
  bills?: Array<{ id: string; billed: number; linked: boolean }>;
}

export interface BudgetLinkedDocs {
  count: number; builder_cost: number; client_cost: number; by_cost_code?: Record<string, number>; unassigned?: number;
  documents?: Record<string, Array<{ id: string; builder: number }>>;
  client_cost_code?: string | null;
  client_documents?: Array<{ id: string; client: number }>;
}

export interface BudgetFinancials {
  /** keyed by cost code id */
  by_cost_code: Record<string, CostCodeFinancials>;
  unassigned: CostCodeFinancials;
  /** every cost code (id) that the eligible sources mention -- purchase orders in any state but voided / cancelled; the Budget shows each as a line */
  source_codes?: string[];
  /** names of the documents behind the figures */
  documents?: { purchase_orders: Record<string, { label: string; status: string }>; bills: Record<string, { label: string; status: string }>; change_orders?: Record<string, { label: string; status: string }>; selections?: Record<string, { label: string; status: string }>; material_orders?: Record<string, { label: string; status: string; purchase_order_id?: string | null }> };
  totals: CostCodeFinancials;
  /** the material orders no purchase order stands for: part of the Budget (same shape as the selections'), never of Committed; `replaced` = the ones a standing purchase order carries now */
  material_orders?: BudgetLinkedDocs & { replaced?: Array<Record<string, unknown>> };
  /**
   * the project's change orders in any state but rejected / cancelled (drafts included): their builder cost is part of the Budget. `by_cost_code` is the part that
   * sits on the cost codes of their items (shown in the Budget column of that code's "POs / Bills / Change Orders" line); `unassigned` is what no item explains
   * (a change order from before items existed). They add up to builder_cost.
   */
  /** the selections that take part (a selected option each): their items' builder cost by cost code, their Client Price on the default code. Same shape as change_orders. */
  selections?: BudgetLinkedDocs;
  change_orders: {
    count: number; builder_cost: number; client_cost: number; by_cost_code?: Record<string, number>; unassigned?: number;
    /** the change orders behind each code's builder cost (by cost code id) */
    documents?: Record<string, Array<{ id: string; builder: number }>>;
    /** The CLIENT price of a change order is one figure per change order, all on this cost code (the company's default; null = it has none). Never part of the builder cost or of Committed. */
    client_cost_code?: string | null;
    client_documents?: Array<{ id: string; client: number }>;
  };
}

async function readJson(response: Response, failure: string) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || failure);
  return body;
}

export async function fetchBudgetOverviewPage(query: EntityQuery, page: { limit: number; offset: number }) {
  const body = await readJson(await fetch(getApiUrl(`api/budgets/overview?${entityQueryParams(query, page)}`)), 'Failed to load the budget overview');
  const data = body.data as { rows: BudgetOverviewRow[]; total: number; summary: BudgetOverviewSummary };
  return {
    rows: data.rows,
    total: Number(body.total ?? data.rows.length),
    hasMore: body.has_more === true,
    extra: { summary: data.summary },
  };
}

/** EVERY project row the query matches (the CSV export): the pages are read one after the other. */
export async function fetchBudgetOverviewAll(query: EntityQuery): Promise<BudgetOverviewRow[]> {
  const rows: BudgetOverviewRow[] = [];
  for (let offset = 0; ; offset += 100) {
    const page = await fetchBudgetOverviewPage(query, { limit: 100, offset });
    rows.push(...page.rows);
    if (!page.hasMore) return rows;
  }
}

export async function fetchBudgetFinancials(projectId: string): Promise<BudgetFinancials> {
  const body = await readJson(await fetch(getApiUrl(`api/budgets/financials?project_id=${encodeURIComponent(projectId)}`)), 'Failed to load the committed and actual costs');
  return body.data as BudgetFinancials;
}
